import type { ContentRegistry } from '../content/registry';
import type { EraDef } from '../content/types';
import type { SimEvent } from '../core/events';
import type { Sim } from '../sim/sim';
import { Environment } from './environment';
import { Particles, PlayerAvatar } from './effects';
import { toonMaterial } from './geometry';
import { InstancedViews } from './instanced';
import { ModelLibrary } from './models';
import { Renderer } from './renderer';
import type { AudioHooks } from '../audio/audio';
import { World, wrapDelta } from '../sim/world';
import { EraTransition } from './eraTransition';
import { PortalViews } from './portals';
import { Entrances } from './entrances';

const prefersReducedMotion = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
import { ERA_TRANSITION_SECONDS, transitionFrame } from './transitionTiming';
import { CharacterLibrary } from './characters';
import { characterAtOffset, type CharacterId } from '../content/characters';
import { CylinderGeometry, Group, Mesh, MeshBasicMaterial, RingGeometry } from 'three';
import type { ObservatorySession } from '../house/observatory';
import type { ItemDef } from '../content/types';
import { projectileLook } from './combatVisuals';
import { enemyProjectileLook } from './enemyProjectileVisuals';
import { ObservatoryView } from './observatory';
import { BossFinishers } from './bossFinishers';
import { PlayerGlow } from './playerGlow';

// When no sim is active (menus/hub) we still render the environment; the
// instanced views need a world to read, so use an empty one.
const EMPTY_WORLD = new World(0);

const PLAYER_COLORS = [0x62e5ff, 0xffb35c, 0xb48cff, 0x7dff9a];

/**
 * GameView: the presentation layer. Reads sim state each frame (never writes
 * it) and turns sim events into particles, shake, audio and warp effects.
 */
export class GameView {
  readonly renderer: Renderer;
  readonly models = new ModelLibrary();
  readonly characters = new CharacterLibrary();
  /** Built by `init()`, which must run (after `models.preload()`) before
   * anything else touches the view — see `Game.start()`. */
  instanced!: InstancedViews;
  environment!: Environment;
  readonly particles: Particles;
  readonly transition: EraTransition;
  readonly portals: PortalViews;
  readonly entrances: Entrances;
  readonly observatory: ObservatoryView;
  readonly bossFinishers: BossFinishers;
  readonly playerGlow: PlayerGlow;
  private readonly objectiveView = new Group();
  private readonly objectiveRing: Mesh;
  private readonly objectiveCore: Mesh;
  private avatars: PlayerAvatar[] = [];
  private era: EraDef | null = null;
  private time = 0;
  private warpTarget = 0;
  /** Active 3D time-jump transition (null when idle). See `timeJump()`. */
  private jump: { t: number; era: EraDef; switched: boolean; onDone: () => void; baseDist: number; basePitch: number } | null = null;
  static readonly JUMP_DURATION = ERA_TRANSITION_SECONDS;
  /**
   * Online co-op: the local player's index. The camera follows only that
   * player (while alive) instead of the party centroid, because remote players
   * are on their own screens. null = local play (follow the living centroid).
   */
  followPlayer: number | null = null;
  /** Settings-driven toggles (Game writes these). */
  shakeEnabled = true;
  damageNumbers: { enabled: boolean; spawn(x: number, z: number, dmg: number, crit: boolean, side: 0 | 1): void; update(dt: number, cx: number, cz: number, half: number): void; clear(): void } | null = null;
  /** Set by Game: called with a title for full-screen announcements (boss phases). */
  onAnnounce: ((text: string) => void) | null = null;

  constructor(canvas: HTMLCanvasElement, readonly reg: ContentRegistry, readonly audio: AudioHooks, overlayRoot?: HTMLElement) {
    this.renderer = new Renderer(canvas);
    this.particles = new Particles(this.renderer.scene);
    this.bossFinishers = new BossFinishers(this.renderer.scene, this.particles);
    this.playerGlow = new PlayerGlow(this.renderer.scene);
    this.transition = new EraTransition(overlayRoot);
    this.portals = new PortalViews(this.renderer.scene, this.renderer, reg, overlayRoot);
    this.entrances = new Entrances(this.renderer.scene, this.particles, audio, (s) => { if (this.shakeEnabled) this.shake(s); });
    this.observatory = new ObservatoryView(this.renderer.scene);
    this.objectiveRing = new Mesh(new RingGeometry(4.6, 5, 64), new MeshBasicMaterial({ color: 0x6fffd2, transparent: true, opacity: 0.42, depthWrite: false }));
    this.objectiveRing.rotation.x = -Math.PI / 2;
    this.objectiveCore = new Mesh(new CylinderGeometry(0.65, 0.9, 2.4, 12), toonMaterial(0x6fffd2));
    this.objectiveCore.position.y = 1.2;
    this.objectiveView.add(this.objectiveRing, this.objectiveCore);
    this.objectiveView.visible = false;
    this.renderer.scene.add(this.objectiveView);
  }

  /** Builds everything that depends on `models` (enemy/prop/player meshes).
   * Call once, after `await this.models.preload(reg)`, before `setEra`/
   * `setPlayerCount`/`frame`. */
  init(): void {
    this.instanced = new InstancedViews(this.renderer.scene, this.reg, this.models);
    this.environment = new Environment(this.renderer.scene, this.models);
    this.renderer.onQualityChange = (q) => {
      this.instanced.outlines = q !== 'low';
      this.instanced.projectileTrails = q !== 'low';
      this.environment.setOutlines(q !== 'low');
    };
    this.renderer.onQualityChange(this.renderer.quality);
  }

  setPlayerCount(n: number, roster: readonly CharacterId[] | unknown = 'knight', equipment: readonly (readonly ItemDef[])[] = []): void {
    for (const a of this.avatars) a.dispose(this.renderer.scene);
    this.avatars = [];
    for (let i = 0; i < n; i++) {
      const character = Array.isArray(roster) ? characterAtOffset(roster[i] ?? 'knight', 0) : characterAtOffset(roster, i);
      this.avatars.push(new PlayerAvatar(this.renderer.scene, PLAYER_COLORS[i % 4], toonMaterial(0xffffff), this.models, this.characters, character.id, equipment[i]));
    }
  }

  setEra(era: EraDef): void {
    if (this.era === era) return;
    this.era = era;
    this.renderer.applyEra(era);
    this.environment.build(era);
    this.instanced?.setEra?.(era.index);
    this.audio.setEra(era);
  }

  enterObservatory(session: ObservatorySession, character: CharacterId, equipment: readonly ItemDef[] = []): void {
    this.environment.group.visible = false;
    this.observatory.build(session);
    this.observatory.activate(this.renderer);
    this.setPlayerCount(1, [character], [equipment]);
    this.instanced.setBoss(null);
    this.objectiveView.visible = false;
    this.renderer.camTarget.set(session.player.x, 0, session.player.z);
  }

  leaveObservatory(): void {
    this.observatory.hide();
    this.environment.group.visible = true;
    if (this.era) this.renderer.applyEra(this.era);
  }

  frameObservatory(session: ObservatorySession, alpha: number, frameDt: number): void {
    this.time += frameDt;
    this.renderer.warp += (this.warpTarget - this.renderer.warp) * Math.min(1, frameDt * 4);
    const p = session.player;
    const x = p.px + (p.x - p.px) * alpha;
    const z = p.pz + (p.z - p.pz) * alpha;
    this.objectiveView.visible = false;
    this.playerGlow?.hide();
    this.instanced.update(EMPTY_WORLD, 0, frameDt, x, z, this.renderer.camera.quaternion, this.renderer.camDistance);
    this.portals.update(null, frameDt, x, z);
    this.entrances.apply(null, this.avatars, false);
    this.avatars[0]?.update(x, z, p.facing, p.moving, p.dashing, false, 0, 0, true, this.time, frameDt);
    this.observatory.update(session, frameDt);
    this.particles.update(frameDt, x, z, 1e9);
    this.renderer.render(x, z, frameDt);
  }

  playObservatoryAbility(session: ObservatorySession): void {
    const p = session.player;
    const color = session.character === 'barbarian' ? 0xffb35c : session.character === 'mage' ? 0xb48cff : session.character === 'ranger' ? 0x7dff9a : 0x62e5ff;
    if (session.character === 'knight' || session.character === 'rogue') {
      this.particles.dashWake(p.x, p.z, Math.cos(p.facing), Math.sin(p.facing), color);
      this.audio.play('dash');
    } else {
      this.particles.burst(p.x, p.z, session.character === 'barbarian' ? 30 : 18, color, session.character === 'barbarian' ? 9 : 6, 0.45, 0.18, 2);
      this.audio.play('shoot');
    }
  }

  /** Time-warp intensity 0..1 (chromatic aberration + ripple). */
  setWarp(v: number): void {
    this.warpTarget = v;
  }

  /**
   * 3D time-jump: the camera pulls up and back while the current era's props
   * dissolve into particles, the warp peaks as we "fall" through, then the new
   * era assembles under a camera that drops back in. 2.8 s; `skipTimeJump()`
   * cuts to the end. Drives `Environment.presence`, `Renderer.camDistance/camPitch`
   * and `warp` from `frame()`; the sim should be held while it runs.
   */
  timeJump(era: EraDef, onDone: () => void): void {
    if (this.jump) this.finishJump();
    this.jump = { t: 0, era, switched: false, onDone, baseDist: this.renderer.camDistance, basePitch: this.renderer.camPitch };
    this.transition.start(era, this.renderer.quality === 'low');
    this.damageNumbers?.clear();
    this.audio.play('timeJump');
  }

  get jumping(): boolean {
    return this.jump !== null;
  }

  skipTimeJump(): void {
    if (this.jump) this.finishJump();
  }

  private finishJump(): void {
    const j = this.jump!;
    this.jump = null;
    this.transition.clear();
    if (!j.switched) this.setEra(j.era);
    this.environment.presence = 1;
    this.renderer.camDistance = j.baseDist;
    this.renderer.camPitch = j.basePitch;
    this.warpTarget = 0;
    this.renderer.warp = 0;
    j.onDone();
  }

  private stepJump(frameDt: number): void {
    const j = this.jump!;
    j.t += frameDt;
    const f = transitionFrame(j.t);
    // This also switches correctly when a long frame skips the middle interval.
    if (f.switched && !j.switched) { j.switched = true; this.setEra(j.era); }
    if (f.done) { this.finishJump(); return; }
    this.renderer.camDistance = j.baseDist + 30 * f.lift;
    this.renderer.camPitch = j.basePitch + (Math.PI / 2 - j.basePitch) * 0.7 * f.lift;
    this.environment.presence = f.presence;
    this.warpTarget = 0.55 * f.lift;
    this.transition.update(f.progress);
  }

  private shake(strength: number): void {
    if (this.shakeEnabled) this.renderer.addShake(strength);
  }

  handleEvents(events: SimEvent[], sim: Sim): void {
    const accent = this.era?.palette.accent ?? 0x7df9ff;
    const enemyCol = this.era?.palette.enemyBullet ?? 0xff3fb0;
    for (const ev of events) {
      switch (ev.type) {
        case 'hit':
          this.damageNumbers?.spawn(ev.x, ev.y, ev.dmg, ev.crit, ev.side);
          if (ev.projectile && ev.side === 0) {
            const look = projectileLook(ev.projectile.color, ev.projectile.visual, ev.projectile.elements);
            this.particles.burst(ev.x, ev.y, ev.crit ? look.impactCount + 4 : look.impactCount, ev.crit ? 0xfff29a : look.color, ev.crit ? 8 : 5.5, ev.crit ? 0.45 : 0.32, ev.crit ? 0.2 : 0.13, 2.5);
          } else if (ev.crit) this.particles.burst(ev.x, ev.y, 6, 0xfff29a, 7, 0.4, 0.2);
          else if (Math.random() < 0.5) this.particles.burst(ev.x, ev.y, 2, accent, 4, 0.25, 0.12);
          this.audio.play(ev.crit ? 'crit' : 'hit', ev.x, ev.y);
          break;
        case 'kill': {
          const def = this.reg.enemies.get(ev.enemyDef);
          this.particles.burst(ev.x, ev.y, ev.elite ? 40 : 12, def?.visual.color ?? enemyCol, ev.elite ? 9 : 6, 0.6, ev.elite ? 0.3 : 0.2);
          this.audio.play(ev.elite ? 'killElite' : 'kill', ev.x, ev.y);
          if (ev.elite) this.shake(0.3);
          break;
        }
        case 'playerHit':
          if (ev.projectile) {
            const look = enemyProjectileLook(ev.projectile.color, ev.projectile.visual, sim.run.eraIndex);
            this.particles.burst(sim.world.players[ev.player].x, sim.world.players[ev.player].y, look.impactCount, ev.projectile.color, 5.5, 0.45, 0.2);
          } else this.particles.burst(sim.world.players[ev.player].x, sim.world.players[ev.player].y, 10, 0xff4a5a, 5, 0.4, 0.2);
          this.damageNumbers?.spawn(sim.world.players[ev.player].x, sim.world.players[ev.player].y, ev.dmg, false, 1);
          this.audio.play('playerHit');
          break;
        case 'playerDeath':
          this.particles.burst(sim.world.players[ev.player].x, sim.world.players[ev.player].y, 80, 0xff4a5a, 10, 1.2, 0.3);
          this.shake(1);
          this.audio.play('playerDeath');
          break;
        case 'dash':
          this.particles.dashWake(ev.x, ev.y, ev.dx, ev.dy);
          this.particles.burst(ev.x, ev.y, 6, 0xffffff, 4, 0.2, 0.1, 1);
          this.shake(0.08);
          this.audio.play('dash');
          break;
        case 'ability': {
          const color = ev.ability === 'warCry' ? 0xffb35c : ev.ability === 'arcaneNova' ? 0xb48cff : ev.ability === 'piercingVolley' ? 0x7dff9a : 0x62e5ff;
          this.particles.burst(ev.x, ev.y, ev.ability === 'warCry' ? 30 : 18, color, ev.ability === 'warCry' ? 9 : 6, 0.45, 0.18, 2);
          if (ev.ability === 'phaseStep') this.particles.dashWake(ev.x, ev.y, ev.dx, ev.dy);
          this.shake(ev.ability === 'warCry' ? 0.35 : 0.12);
          this.audio.play(ev.ability === 'phaseStep' ? 'dash' : 'shoot');
          break;
        }
        case 'levelUp':
          this.particles.burst(sim.world.players[ev.player].x, sim.world.players[ev.player].y, 40, 0x5dff9c, 8, 0.8, 0.25, 6);
          this.audio.play('levelUp');
          break;
        case 'pickup':
          if (ev.kind !== 'xp') this.audio.play(ev.kind === 'heal' ? 'heal' : 'shard');
          else if (Math.random() < 0.3) this.audio.play('xp');
          break;
        case 'bossSpawn': {
          const def = this.reg.bosses.get(ev.defId);
          this.instanced.setBoss(def ?? null);
          this.onAnnounce?.(def ? `${def.name}` : 'Boss');
          this.audio.play('bossSpawn');
          this.audio.setMusic('boss');
          break;
        }
        case 'bossPhase': {
          this.audio.play('bossPhase');
          const b = sim.ctx.boss;
          const name = b ? b.def.phases[ev.phase]?.name ?? `Phase ${ev.phase + 1}` : `Phase ${ev.phase + 1}`;
          this.onAnnounce?.(name);
          this.renderer.warp = Math.max(this.renderer.warp, 0.5);
          break;
        }
        case 'bossDeath': {
          const profile = this.bossFinishers.start(ev.defId, ev.x, ev.y);
          if (!profile) this.particles.burst(ev.x, ev.y, 120, 0xffffff, 12, 1.5, 0.35, 8);
          else {
            this.onAnnounce?.(profile.title);
            this.renderer.warp = Math.max(this.renderer.warp, profile.warp);
            this.shake(profile.shake);
          }
          this.audio.play('bossDeath');
          this.audio.setMusic('stage');
          break;
        }
        case 'enemyFire': {
          const look = enemyProjectileLook(ev.projectile.color, ev.projectile.visual, sim.run.eraIndex);
          this.particles.burst(ev.x, ev.y, Math.max(4, Math.floor(look.impactCount * .45)), ev.projectile.color, 4, .25, .1, 2);
          break;
        }
        case 'shake':
          this.shake(ev.strength);
          break;
        case 'portalsOpen':
          this.audio.play('rewind');
          this.onAnnounce?.(sim.run.phase === 'eraClear' ? 'Choose your portal' : 'Portal open');
          break;
        case 'portalBenefit':
          this.audio.play('pickup');
          this.onAnnounce?.(ev.title);
          break;
        case 'sfx':
          if (ev.id === 'shoot') {
            this.audio.play('shoot', ev.x, ev.y);
            if (ev.x !== undefined && ev.y !== undefined && ev.projectile) {
              const look = projectileLook(ev.projectile.color, ev.projectile.visual, ev.projectile.elements);
              this.particles.burst(ev.x, ev.y, look.launchCount, look.color, 4.5, 0.22, 0.1, 2);
            }
          }
          else if (ev.id === 'rewind') {
            this.audio.play('rewind');
            this.renderer.warp = Math.max(this.renderer.warp, 0.8);
          } else this.audio.play(ev.id, ev.x, ev.y);
          break;
        default:
          break;
      }
    }
  }

  frame(sim: Sim | null, alpha: number, frameDt: number): void {
    this.time += frameDt;
    this.renderer.warp += (this.warpTarget - this.renderer.warp) * Math.min(1, frameDt * 4);
    const camX = this.renderer.camTarget.x;
    const camZ = this.renderer.camTarget.z;
    let cx = camX;
    let cz = camZ;
    if (sim) {
      const w = sim.world;
      const half = w.half;
      this.instanced.update(w, alpha, frameDt, camX, camZ, this.renderer.camera.quaternion, this.renderer.camDistance);
      const objective = sim.currentStage.objective;
      const showObjective = sim.run.phase === 'playing' && (objective?.type === 'hold' || objective?.type === 'defend' || objective?.type === 'escort');
      if (this.objectiveView) this.objectiveView.visible = showObjective;
      if (showObjective && this.objectiveView && this.objectiveRing && this.objectiveCore) {
        this.objectiveView.position.set(camX + wrapDelta(sim.run.objectiveX - camX, half), 0.05, camZ + wrapDelta(sim.run.objectiveY - camZ, half));
        this.objectiveView.rotation.y += frameDt * 0.45;
        const radius = objective.radius ?? (objective.type === 'escort' ? 5 : 6);
        this.objectiveRing.scale.setScalar(radius / 5);
        this.objectiveCore.visible = objective.type !== 'hold';
        const health = Math.max(0.15, sim.run.objectiveHealth / (objective.type === 'hold' ? 100 : objective.health ?? 100));
        this.objectiveCore.scale.y = health;
      }
      // follow the wrapped centroid of living players (relative to player 0 so co-op never averages across the seam)
      const anchor = this.followPlayer !== null && w.players[this.followPlayer] ? this.followPlayer : 0;
      const p0 = w.players[anchor];
      const ax = p0.px + (p0.x - p0.px) * alpha;
      const az = p0.py + (p0.y - p0.py) * alpha;
      let sx = 0;
      let sz = 0;
      let n = 0;
      const glowPlayers: { x: number; z: number; alive: boolean }[] = [];
      for (let i = 0; i < w.players.length; i++) {
        const p = w.players[i];
        const x = p.px + (p.x - p.px) * alpha;
        const z = p.py + (p.y - p.py) * alpha;
        const firing = p.alive && !p.moving && p.dashT <= 0 && p.targetEnemy !== 0;
        const renderX = camX + wrapDelta(x - camX, half);
        const renderZ = camZ + wrapDelta(z - camZ, half);
        this.avatars[i]?.update(renderX, renderZ, p.facing, p.moving, p.dashT > 0, firing, p.hitFlash, p.iframes, p.alive, this.time, frameDt);
        glowPlayers.push({ x: renderX, z: renderZ, alive: p.alive });
        if (p.alive) {
          sx += wrapDelta(x - ax, half);
          sz += wrapDelta(z - az, half);
          n++;
        }
      }
      this.playerGlow?.update(this.era?.index ?? null, this.renderer.quality, this.renderer.reducedFlash, glowPlayers);
      this.entrances.apply(sim, this.avatars, this.renderer.reducedFlash || prefersReducedMotion());
      if (this.followPlayer !== null && p0.alive) {
        cx = ax;
        cz = az;
      } else if (n > 0) {
        cx = ax + sx / n;
        cz = az + sz / n;
      }
      this.environment.setStage(sim.run.stageIndex);
      this.environment.update(frameDt, camX, camZ);
      this.particles.update(frameDt, camX, camZ, half);
      this.damageNumbers?.update(frameDt, camX, camZ, half);
      this.portals.update(sim, frameDt, camX, camZ);
    } else {
      if (this.objectiveView) this.objectiveView.visible = false;
      this.playerGlow?.hide();
      this.portals.update(null, frameDt, camX, camZ);
      this.entrances.apply(null, this.avatars, false);
      this.instanced.update(EMPTY_WORLD, 0, frameDt, camX, camZ, this.renderer.camera.quaternion, this.renderer.camDistance);
      this.environment.update(frameDt, camX, camZ);
      this.particles.update(frameDt, camX, camZ, 1e9);
    }
    if (this.jump) this.stepJump(frameDt);
    this.bossFinishers?.update(frameDt, this.instanced.reducedFlash || prefersReducedMotion());
    this.renderer.render(cx, cz, frameDt);
  }
}
